import { PUBLIC_ROUTES } from '@/configs/routes';
import { SEARCH_PARAM } from '@/constants/searchParams';

/** Query param carrying serialized filter/sort state on public search pages. */
export const PUBLIC_SEARCH_STATE_PARAM = 'search';

/** Params preserved when navigating between sale listing and animal detail. */
export const CATALOGUE_CONTEXT_PARAMS = [
  PUBLIC_SEARCH_STATE_PARAM,
  SEARCH_PARAM.LOT_NO,
] as const;

function copyCatalogueContextParams(
  source: URLSearchParams | string | null | undefined,
): URLSearchParams {
  const params = new URLSearchParams(
    typeof source === 'string' ? source : (source?.toString() ?? ''),
  );
  params.delete(SEARCH_PARAM.TAB);
  params.delete(SEARCH_PARAM.SALE_ID);
  return params;
}

/**
 * Build animal detail href with sale catalogue context for lot browsing.
 */
export function buildSaleLotAnimalHref(
  orgCode: string,
  animalId: number | string,
  options: {
    saleId: string | number;
    lotNo: string;
    searchParams?: URLSearchParams | string | null;
  },
): string {
  const params = copyCatalogueContextParams(options.searchParams);
  params.set(SEARCH_PARAM.SALE_ID, String(options.saleId));
  params.set(SEARCH_PARAM.LOT_NO, options.lotNo);
  const qs = params.toString();
  return `/${orgCode}${PUBLIC_ROUTES.ANIMAL}/${animalId}${qs ? `?${qs}` : ''}`;
}

/**
 * Build sale listing href, optionally anchoring to a lot via lotNo query param.
 */
export function buildSaleListingHref(
  orgCode: string,
  saleId: string | number,
  searchParams?: URLSearchParams | string | null,
  options?: { lotNo?: string },
): string {
  const params = copyCatalogueContextParams(searchParams);
  if (options?.lotNo) {
    params.set(SEARCH_PARAM.LOT_NO, options.lotNo);
  } else {
    params.delete(SEARCH_PARAM.LOT_NO);
  }
  const qs = params.toString();
  return `/${orgCode}/sales/${saleId}${qs ? `?${qs}` : ''}`;
}

/**
 * Params to preserve on the animal detail URL when paging between lots.
 */
export function buildCatalogueAnimalQueryString(
  saleId: string | number,
  lotNo: string,
  searchParams?: URLSearchParams | string | null,
  tab?: string | null,
): string {
  const params = copyCatalogueContextParams(searchParams);
  params.set(SEARCH_PARAM.SALE_ID, String(saleId));
  params.set(SEARCH_PARAM.LOT_NO, lotNo);
  if (tab) {
    params.set(SEARCH_PARAM.TAB, tab);
  }
  return params.toString();
}
